/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Images
import curved11 from "assets/images/curved-images/curved11.jpg";

// Components
import Box from "components/Atoms/Box";
import ButtonV1 from "components/Atoms/Button/ButtonV1";
import Input from "components/Atoms/Input";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";
import CoverLayout from "components/LayoutContainers/CoverLayout";
import Logo from "components/Logo";
import Icon from "@mui/material/Icon";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, selectThemeMode } from "state/application/applicationSlice";
import { useUpdateAccountMutation } from "state/auth/accountApiSlice";
import { setCredentials } from "state/auth/authSlice";

function ChangePassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useParams();
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";

  const [buttonText, setButtonText] = useState("Continuar");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const canSave = token && [password, repeatPassword].every(Boolean) && password === repeatPassword;

  // UPDATE Password
  const [changePassword] = useUpdateAccountMutation();
  const handleChangePassword = async () => {
    if (canSave) {
      setButtonText("Changing Password");
      try {
        const userData = await changePassword({ password }).unwrap();
        dispatch(setCredentials({ access_token: token, ...userData }));
        return navigate("/communities");
      } catch (err) {
        return dispatch(openNotificationPopup({ notification: err, type: "error" }));
      }
    }
    return null;
  };

  useEffect(() => {
    const credentials = { access_token: token };
    dispatch(setCredentials(credentials));
  }, [token]);

  return (
    <CoverLayout
      image={curved11}
      header={
        <Box mb={2}>
          <Box mb={2}>
            <Logo size="medium" showSubtitle />
          </Box>
          <TypographyV1
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              color: "#10b981",
              background: "linear-gradient(195deg, #10b981, #059669)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Olá!
          </TypographyV1>
          <TypographyV1
            variant="body2"
            fontWeight="regular"
            sx={{ color: isDark ? "#cbd5e0" : "#718096" }}
          >
            Introduz uma password para acederes à tua comunidade
          </TypographyV1>
        </Box>
      }
    >
      <Box mb={2} lineHeight={1.25}>
        <Box mb={1} ml={0.5}>
          <TypographyV1 component="label" variant="caption" fontWeight="bold">
            Password
          </TypographyV1>
        </Box>
        <Input
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          type="password"
          placeholder="Password"
        />
      </Box>
      <Box mb={2} lineHeight={1.25}>
        <Box mb={1} ml={0.5}>
          <TypographyV1 component="label" variant="caption" fontWeight="bold">
            Repetir Password
          </TypographyV1>
        </Box>
        <Input
          onChange={(event) => {
            setRepeatPassword(event.target.value);
          }}
          type="password"
          placeholder="Password"
        />
      </Box>
      <Box mt={4} mb={1}>
        <ButtonV1
          onClick={handleChangePassword}
          variant="contained"
          buttonColor="dark"
          fullWidth
          disabled={!canSave}
          sx={{
            backgroundColor: "#059669 !important",
            minHeight: "50px",
            fontSize: "1rem",
            textTransform: "none",
          }}
        >
          <Icon sx={{ mr: 1.5, fontSize: "1.5rem !important" }}>lock_reset</Icon>
          {buttonText}
        </ButtonV1>
      </Box>
    </CoverLayout>
  );
}

export default ChangePassword;
